import React, { useState } from 'react';
import { Box, Button, Paper, Typography, Alert, CircularProgress } from '@mui/material';
import { useDirectus } from '../providers/DirectusProvider';
import { usePWA } from '../providers/PWAProvider';

export const JWTTestComponent: React.FC = () => {
  const { client, auth, accessToken } = useDirectus();
  const { status } = usePWA();
  const [testResult, setTestResult] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);

  const testJWTRefresh = async () => {
    setIsLoading(true);
    setTestResult('');

    try {
      console.log('🧪 Testing JWT refresh on 401...');
      
      // Simulate an API call that will trigger 401 by using an invalid token
      const originalToken = accessToken;
      
      // Temporarily set an invalid token to force 401
      client.setToken('invalid_token_to_force_401');
      
      console.log('📡 Making API call with invalid token...');
      
      // Try to fetch user info - this should trigger 401 and then refresh
      const response = await client.request({
        method: 'GET',
        path: '/users/me'
      });
      
      console.log('✅ API call succeeded after token refresh:', response);
      setTestResult('✅ JWT refresh on 401 working correctly! Token was automatically refreshed.');
      
    } catch (error: any) {
      console.error('❌ JWT refresh test failed:', error);
      setTestResult(`❌ JWT refresh test failed: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const testServiceWorkerAuth = async () => {
    setIsLoading(true);
    setTestResult('');

    try {
      console.log('🧪 Testing Service Worker authentication...');
      
      
      if (!navigator.serviceWorker.controller) {
        throw new Error('No Service Worker controller available');
      }

      // Send a message to Service Worker to trigger a background sync
      navigator.serviceWorker.controller.postMessage({
        type: 'TRIGGER_SYNC'
      });

      console.log('📡 Background sync triggered');
      setTestResult('🔄 Background sync triggered. Check console and network tab for authentication flow.');
      
    } catch (error: any) {
      console.error('❌ Service Worker auth test failed:', error);
      setTestResult(`❌ Service Worker test failed: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const simulateTokenExpiry = async () => {
    setIsLoading(true);
    setTestResult('');

    try {
      console.log('🧪 Simulating token expiry...');
      
      // Manually trigger token refresh
      const newToken = await auth.refreshAccessToken();
      
      if (newToken) {
        console.log('✅ Token refresh successful:', newToken);
        setTestResult('✅ Manual token refresh successful!');
      } else {
        throw new Error('Token refresh returned null');
      }
      
    } catch (error: any) {
      console.error('❌ Token refresh failed:', error);
      setTestResult(`❌ Token refresh failed: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        🔐 JWT Token Refresh Test Dashboard
      </Typography>
      
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Current Token: {accessToken ? `${accessToken.substring(0, 20)}...` : 'None'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          User: {auth.user?.email || 'Not authenticated'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          PWA Status: {status.isPWASupported ? '✅ Supported' : '❌ Not supported'}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Button
          variant="contained"
          onClick={testJWTRefresh}
          disabled={isLoading || !auth.isAuthenticated}
        >
          {isLoading ? <CircularProgress size={20} /> : '🧪 Test JWT Refresh on 401'}
        </Button>

        <Button
          variant="contained"
          color="secondary"
          onClick={testServiceWorkerAuth}
          disabled={isLoading || !status.isPWASupported}
        >
          {isLoading ? <CircularProgress size={20} /> : '🔄 Test SW Background Sync'}
        </Button>

        <Button
          variant="outlined"
          onClick={simulateTokenExpiry}
          disabled={isLoading || !auth.isAuthenticated}
        >
          {isLoading ? <CircularProgress size={20} /> : '🔄 Manual Token Refresh'}
        </Button>
      </Box>

      {testResult && (
        <Alert 
          severity={testResult.startsWith('✅') ? 'success' : testResult.startsWith('🔄') ? 'info' : 'error'}
          sx={{ mt: 2 }}
        >
          {testResult}
        </Alert>
      )}

      <Box sx={{ mt: 2 }}>
        <Typography variant="caption" color="text.secondary">
          💡 These tests verify that JWT tokens are refreshed automatically on 401 responses, 
          both in the main app and in Service Worker background sync operations.
        </Typography>
      </Box>
    </Paper>
  );
};

export default JWTTestComponent;
